/**
 * Email Connection Service
 * TASK-270: Email OAuth integration for search
 */

import { API_CONFIG } from '../config/api-config';
import type {
  EmailProvider,
  EmailConnectionStatus,
} from '../types/emailConnection.types';

class EmailConnectionService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_CONFIG.GATEWAY_URL;
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  /**
   * Get email connection status for all providers
   */
  async getStatus(): Promise<EmailConnectionStatus> {
    const response = await fetch(`${this.baseUrl}/api/search/email/status`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to get email connection status');
    }

    return response.json();
  }

  /**
   * Start OAuth flow for a provider
   * Opens popup window for OAuth authorization
   */
  async connect(provider: EmailProvider): Promise<void> {
    return new Promise((resolve, reject) => {
      // Get OAuth URL from backend
      fetch(`${this.baseUrl}/api/search/email/oauth/${provider}`, {
        headers: this.getAuthHeaders(),
      })
        .then((res) => res.json())
        .then((data) => {
          const authUrl = data.auth_url;
          if (!authUrl) {
            throw new Error('Failed to get authorization URL');
          }

          // Open popup for OAuth
          const width = 600;
          const height = 700;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;

          const popup = window.open(
            authUrl,
            'email_oauth',
            `width=${width},height=${height},left=${left},top=${top}`
          );

          if (!popup) {
            throw new Error('Failed to open OAuth popup. Please allow popups.');
          }

          // Listen for OAuth callback message
          const handleMessage = (event: MessageEvent) => {
            if (event.data?.type === 'email_oauth_success') {
              window.removeEventListener('message', handleMessage);
              resolve();
            } else if (event.data?.type === 'email_oauth_error') {
              window.removeEventListener('message', handleMessage);
              reject(new Error(event.data.error || 'OAuth failed'));
            }
          };

          window.addEventListener('message', handleMessage);

          // Check if popup was closed without completing OAuth
          const checkClosed = setInterval(() => {
            if (popup.closed) {
              clearInterval(checkClosed);
              window.removeEventListener('message', handleMessage);
              // Don't reject - user might have completed OAuth
            }
          }, 1000);
        })
        .catch(reject);
    });
  }

  /**
   * Disconnect an email provider
   */
  async disconnect(provider: EmailProvider): Promise<void> {
    const response = await fetch(
      `${this.baseUrl}/api/search/email/disconnect/${provider}`,
      {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to disconnect email provider');
    }
  }

  /**
   * Trigger manual email sync
   */
  async triggerSync(): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/search/email/sync`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to trigger email sync');
    }
  }
}

export const emailConnectionService = new EmailConnectionService();
export default emailConnectionService;
